import experienceScoreStatuses from '@/consts/experienceScore/experienceScoreStatuses';
import insightDetailTypes, {type InsightDetailType} from '@/consts/experienceScore/insightDetailTypes';
import insightsAndTipsCodes from '@/consts/experienceScore/insightsAndTipsCodes';
import {getContent} from '@/services/content/content';
import {formatPlural, formatTime, getLanguageCode} from '@/services/localization/localizationService';
import type {IconModel, MetricModel} from '@/types/aem';
import type {InsightsAndTipsEntry, TipEntry, TipsToConsider} from '@/types/inference';

import metricMapping from './mappings/metricMapping';

export interface InsightTileSource {
    insightsAndTips?: Record<string, InsightsAndTipsEntry> | null;
    tipsToConsider?: TipsToConsider | null;
    isRetail: boolean;
}

interface TileContext extends InsightTileSource {
    metric: MetricModel;
    entry?: InsightsAndTipsEntry;
    retailValue?: string;
}

interface TileValue {
    value?: string;
    isValueBlurred?: boolean;
}

export interface InsightTileData extends TileValue {
    code: string;
    title?: string;
    icon?: IconModel;
    /** Set when the metric has a detail view - makes the tile clickable. */
    detailType?: InsightDetailType;
    /** Set when the metric is reported as sub optimal. */
    warningIcon?: IconModel;
    isWide: boolean;
}

interface TileResolver {
    detailType?: InsightDetailType;
    /** `undefined` hides the tile: a metric without a value is not shown at all. */
    getValue: (context: TileContext) => TileValue | undefined;
}

const STICK_REUSE_NEVER = 'never';
const STICK_REUSE_ALMOST_NEVER = 'almost_never';

/** A time config means the value is a duration in seconds; everything else is a plain count. */
const formatValue = (value: number, {valueTimeConfig, valuePluralForms}: MetricModel) =>
    valueTimeConfig
        ? formatTime(value, valueTimeConfig, getLanguageCode(), true)
        : formatPlural(Math.round(value), valuePluralForms);

const getTipValue = ({entry, metric}: TileContext): TileValue | undefined => {
    const tip = entry as TipEntry | undefined;

    if (tip?.current_behavior == null) return undefined;

    return {value: formatValue(tip.current_behavior, metric)};
};

/** Retail devices hide the real averages behind a blurred placeholder. */
const getAverageValue = (context: TileContext): TileValue | undefined => {
    const tileValue = getTipValue(context);

    return tileValue && context.isRetail ? {value: context.retailValue, isValueBlurred: true} : tileValue;
};

const getValueLabel = ({valueLabels}: MetricModel, value: string) =>
    valueLabels?.find((valueLabel) => valueLabel.value === value)?.label;

const getStickReuseValue = ({entry, metric}: TileContext): TileValue | undefined => {
    const tip = entry as TipEntry | undefined;

    if (tip?.current_behavior == null) return undefined;

    const reusedSticks = Math.round(tip.current_behavior);

    if (tip.status === experienceScoreStatuses.SUB_OPTIMAL && reusedSticks > 0)
        return {value: formatValue(reusedSticks, metric)};

    return {value: getValueLabel(metric, reusedSticks > 0 ? STICK_REUSE_ALMOST_NEVER : STICK_REUSE_NEVER)};
};

const getBreakdownValue = ({entry, metric}: TileContext): TileValue | undefined => {
    const value = entry && metricMapping.getDominantValueLabel(metric.valueLabels, entry);

    return value ? {value} : undefined;
};

// A tile opens its detail only when that detail is authored - otherwise it is not a button.
const hasDetailContent: Record<InsightDetailType, (code: string) => boolean> = {
    [insightDetailTypes.PAGE]: metricMapping.hasDetail,
    [insightDetailTypes.DRAWER]: metricMapping.hasExplanation,
};

const tileResolvers: Record<string, TileResolver> = {
    [insightsAndTipsCodes.EG002]: {detailType: insightDetailTypes.PAGE, getValue: getTipValue},
    [insightsAndTipsCodes.EG004]: {detailType: insightDetailTypes.PAGE, getValue: getStickReuseValue},
    [insightsAndTipsCodes.AVERAGE_PUFFS]: {detailType: insightDetailTypes.DRAWER, getValue: getAverageValue},
    [insightsAndTipsCodes.AVERAGE_DURATION]: {detailType: insightDetailTypes.DRAWER, getValue: getAverageValue},
    [insightsAndTipsCodes.TIMES_TUNED]: {detailType: insightDetailTypes.DRAWER, getValue: getTipValue},
    [insightsAndTipsCodes.TUNABILITY]: {detailType: insightDetailTypes.DRAWER, getValue: getBreakdownValue},
    [insightsAndTipsCodes.SKU]: {detailType: insightDetailTypes.DRAWER, getValue: getBreakdownValue},
    [insightsAndTipsCodes.RANGE_PROFILE]: {detailType: insightDetailTypes.DRAWER, getValue: getBreakdownValue},
};

const getInsightTiles = (source: InsightTileSource): InsightTileData[] => {
    const {metrics = [], warningIcon, retailValue} = getContent().experienceScoreContent ?? {};
    const tiles = metrics.flatMap((metric) => {
        const code = metric.code ?? '';
        const resolver = tileResolvers[code];

        if (!resolver) return [];

        const tileValue = resolver.getValue({
            ...source,
            metric,
            retailValue,
            entry: source.insightsAndTips?.[code],
        });

        if (!tileValue) return [];

        const {detailType} = resolver;

        return [
            {
                ...tileValue,
                code,
                title: metric.title,
                icon: metric.icon,
                detailType: detailType && hasDetailContent[detailType](code) ? detailType : undefined,
                warningIcon: source.tipsToConsider?.sub_optimal.includes(code) ? warningIcon : undefined,
            },
        ];
    });

    // The grid is always two columns: an odd count widens the first tile instead of leaving the last one alone.
    const isFirstTileWide = tiles.length % 2 === 1;

    return tiles.map((tile, index) => ({...tile, isWide: isFirstTileWide && index === 0}));
};

export default {getInsightTiles};
